// =============================================================================
// ChangeSizeModal — ops/admin/owner swap a confirmed (pre-delivery) order line
// to a different size: a standard sibling variant or a bespoke custom size.
// Pricing is server-authoritative (POST /orders/:id/change-size); this UI only
// previews it: a cheaper size keeps the original amount, a pricier size shows
// the difference and lets the operator bill it now or at delivery.
// =============================================================================
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CUSTOM_SIZE_MIN_CM,
  CUSTOM_SIZE_MAX_CM,
  METHODS_REQUIRING_REFERENCE,
  type PaymentMethod,
} from '@kalachand/shared';
import { apiGet, apiPost, getApiError } from '../../lib/api';
import { useHasPermission } from '../../lib/permissions';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Money } from '../../components/Money';
import { Input } from '../../components/Input';
import { useToast } from '../../components/Toast';

interface Variant {
  id: string;
  size: string;
  fabric: string | null;
  color: string | null;
  sideColor: string | null;
  basePrice: string;
  salePrice: string | null;
  needsPricing: boolean;
  isActive: boolean;
}
interface ModelResp {
  id: string;
  name: string;
  allowsCustomSize: boolean;
  category: { fulfillmentStrategy: string };
  variants: Variant[];
}
interface CustomPrice {
  unitPrice: string;
  vatRate: string;
  matchedSize: string;
  cappedAtLargest: boolean;
  surchargePct: number;
  preSurchargePrice: string;
  // Set when the matched standard size is on promo — the custom size follows it.
  promoApplied: boolean;
  matchedBasePrice: string;
}

const METHODS: PaymentMethod[] = ['cash', 'juice', 'emtel', 'myt', 'bank_transfer', 'card', 'cheque', 'other'];
const variantPrice = (v: Variant): number => Number(v.salePrice ?? v.basePrice);

export function ChangeSizeModal({
  orderId,
  orderNumber,
  line,
  onClose,
}: {
  orderId: string;
  orderNumber: string;
  line: { id: string; productId: string; modelId: string; qty: number; currentGross: number; label: string };
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<'standard' | 'custom'>('standard');
  const [variantId, setVariantId] = useState('');
  const [w, setW] = useState('');
  const [l, setL] = useState('');
  const [dw, setDw] = useState('');
  const [dl, setDl] = useState('');
  const [billing, setBilling] = useState<'at_delivery' | 'now'>('at_delivery');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const canRecordCheque = useHasPermission('payments.record_cheque');
  const methodOptions = canRecordCheque ? METHODS : METHODS.filter((m) => m !== 'cheque');

  useEffect(() => {
    const t = setTimeout(() => { setDw(w); setDl(l); }, 350);
    return () => clearTimeout(t);
  }, [w, l]);

  const modelQ = useQuery({
    queryKey: ['product-model', line.modelId],
    queryFn: () => apiGet<ModelResp>(`/product-models/${line.modelId}`),
  });
  const model = modelQ.data;

  // Sibling standard sizes to switch to — active, priced, and not the current line.
  const siblings = useMemo(
    () => (model?.variants ?? []).filter((v) => v.isActive && !v.needsPricing && v.id !== line.productId),
    [model, line.productId],
  );

  const wn = Number(dw);
  const ln = Number(dl);
  const validDims =
    Number.isInteger(wn) && Number.isInteger(ln) &&
    wn >= CUSTOM_SIZE_MIN_CM && wn <= CUSTOM_SIZE_MAX_CM &&
    ln >= CUSTOM_SIZE_MIN_CM && ln <= CUSTOM_SIZE_MAX_CM;

  const customQ = useQuery({
    queryKey: ['change-size-custom', line.modelId, dw, dl],
    queryFn: () => apiPost<CustomPrice>(`/product-models/${line.modelId}/custom-size/price`, { widthCm: wn, lengthCm: ln }),
    enabled: mode === 'custom' && validDims && !!model?.allowsCustomSize,
  });

  // New per-line total for the chosen size (VAT-inclusive gross).
  const selectedVariant = siblings.find((v) => v.id === variantId);
  const newUnit =
    mode === 'standard'
      ? (selectedVariant ? variantPrice(selectedVariant) : null)
      : (customQ.data ? Number(customQ.data.unitPrice) : null);
  const newGross = newUnit != null ? newUnit * line.qty : null;
  const isUpgrade = newGross != null && newGross > line.currentGross + 0.005;
  const difference = isUpgrade ? newGross! - line.currentGross : 0;
  const isCustomUpgrade = mode === 'custom' && isUpgrade;
  // A custom upgrade must be paid in full immediately (custom items are paid in
  // advance) — force "bill now" so the bespoke line is never left underpaid.
  const effectiveBilling: 'now' | 'at_delivery' = isCustomUpgrade ? 'now' : billing;
  const needsRef = METHODS_REQUIRING_REFERENCE.includes(method);
  const paymentReady = !isUpgrade || effectiveBilling === 'at_delivery' || (!!method && (!needsRef || reference.trim().length > 0));
  const hasTarget = mode === 'standard' ? !!selectedVariant : !!customQ.data;

  const mut = useMutation({
    mutationFn: () => {
      const payment =
        isUpgrade && effectiveBilling === 'now'
          ? { amount: Number(difference.toFixed(2)), method, reference: needsRef ? reference.trim() : undefined }
          : undefined;
      const body =
        mode === 'standard'
          ? { orderLineId: line.id, newProductId: variantId, billing: effectiveBilling, payment, reason: reason.trim() || undefined }
          : { orderLineId: line.id, customWidthCm: wn, customLengthCm: ln, billing: effectiveBilling, payment, reason: reason.trim() || undefined };
      return apiPost(`/orders/${orderId}/change-size`, body);
    },
    onSuccess: () => {
      toast.show('Size changed.', 'success');
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      onClose();
    },
    onError: (e) => toast.show(getApiError(e).message, 'error'),
  });

  return (
    <Modal onClose={onClose} title="Change size" subtitle={`${orderNumber} · ${line.label}`}>
      <div className="space-y-4">
        <p className="text-xs text-ink-3">
          Current charge for this line: <Money value={line.currentGross} className="text-ink" />. A smaller size keeps
          this amount; a bigger size adds the difference.
        </p>

        {/* Mode toggle */}
        <div className="flex gap-2">
          <Button type="button" variant={mode === 'standard' ? 'primary' : 'ghost'} size="sm" onClick={() => setMode('standard')}>
            Standard size
          </Button>
          {model?.allowsCustomSize && (
            <Button type="button" variant={mode === 'custom' ? 'primary' : 'ghost'} size="sm" onClick={() => setMode('custom')}>
              Custom size
            </Button>
          )}
        </div>

        {modelQ.isLoading ? (
          <p className="text-sm text-ink-3">Loading sizes…</p>
        ) : mode === 'standard' ? (
          siblings.length === 0 ? (
            <p className="text-sm text-ink-3">No other standard sizes are available for this model.</p>
          ) : (
            <label className="block text-xs text-ink-3">
              New size
              <select className="kc-input w-full mt-1" value={variantId} onChange={(e) => setVariantId(e.target.value)}>
                <option value="">Select a size…</option>
                {siblings.map((v) => (
                  <option key={v.id} value={v.id}>
                    {[v.size, v.fabric, v.color].filter(Boolean).join(' · ')} — Rs {variantPrice(v).toLocaleString('en-MU')}
                  </option>
                ))}
              </select>
            </label>
          )
        ) : (
          <div className="space-y-2">
            <div className="flex items-end gap-3">
              <label className="flex-1 text-xs text-ink-3">
                Width (cm)
                <input type="number" min={CUSTOM_SIZE_MIN_CM} max={CUSTOM_SIZE_MAX_CM} value={w}
                  onChange={(e) => setW(e.target.value)} className="kc-input w-full mt-1 kc-mono" />
              </label>
              <span className="pb-2 text-ink-4">×</span>
              <label className="flex-1 text-xs text-ink-3">
                Length (cm)
                <input type="number" min={CUSTOM_SIZE_MIN_CM} max={CUSTOM_SIZE_MAX_CM} value={l}
                  onChange={(e) => setL(e.target.value)} className="kc-input w-full mt-1 kc-mono" />
              </label>
            </div>
            {w !== '' && l !== '' && !validDims && (
              <p className="text-xs text-negative">Each side must be a whole number between {CUSTOM_SIZE_MIN_CM} and {CUSTOM_SIZE_MAX_CM} cm.</p>
            )}
            {validDims && customQ.isError && (
              <p className="text-xs text-negative">{getApiError(customQ.error).message}</p>
            )}
            {customQ.data && (
              <p className="text-xs text-ink-3">
                Priced as <span className="kc-mono text-ink">{customQ.data.matchedSize}</span>
                {customQ.data.promoApplied && (
                  <> · <span className="text-accent">on promo</span>, was <Money value={customQ.data.matchedBasePrice} className="line-through" /></>
                )}
                {customQ.data.surchargePct > 0 && <> · +{Math.round(customQ.data.surchargePct * 100)}% special import surcharge</>}
              </p>
            )}
          </div>
        )}

        {/* Price preview */}
        {hasTarget && newGross != null && (
          <div className="rounded-md border border-rule bg-paper-2 px-4 py-3 text-sm">
            {isUpgrade ? (
              <div className="flex items-center justify-between">
                <span className="text-ink-3">New line total</span>
                <span className="text-right">
                  <Money value={newGross} className="text-base font-medium" />
                  <span className="block text-xs text-warning">+<Money value={difference} /> to charge</span>
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-ink-3">Amount stays the same</span>
                <Money value={line.currentGross} className="text-base font-medium" />
              </div>
            )}
          </div>
        )}

        {/* Billing the increase */}
        {isUpgrade && (
          <div className="space-y-2">
            <div className="text-xs text-ink-3">How to charge the difference</div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={effectiveBilling === 'now' ? 'primary' : 'ghost'}
                onClick={() => setBilling('now')}>
                Bill now
              </Button>
              <Button type="button" size="sm" variant={effectiveBilling === 'at_delivery' ? 'primary' : 'ghost'}
                disabled={isCustomUpgrade}
                onClick={() => setBilling('at_delivery')}>
                Add to balance (at delivery)
              </Button>
            </div>
            {isCustomUpgrade && (
              <p className="text-[11px] text-ink-3">A pricier custom size must be paid in full now.</p>
            )}
            {effectiveBilling === 'now' && (
              <div className="flex gap-2">
                <label className="flex-1 text-xs text-ink-3">
                  Method
                  <select className="kc-input w-full mt-1" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                    {methodOptions.map((m) => (
                      <option key={m} value={m}>{m.replace('_', ' ')}</option>
                    ))}
                  </select>
                </label>
                {needsRef && (
                  <label className="flex-1 text-xs text-ink-3">
                    Reference
                    <Input value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1" />
                  </label>
                )}
              </div>
            )}
          </div>
        )}

        <label className="block text-xs text-ink-3">
          Reason (optional)
          <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="e.g. customer requested a bigger size" />
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!hasTarget || !paymentReady || mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Change size'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
